import {
  Alert,
  Button,
  Flex,
  Grid,
  IconEditLine,
  List,
  Pill,
  Text,
  ToggleDetails,
} from "@instructure/ui";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MasteryLevelContext } from "../../../../utils/Context";
import RaterGridAdd from "../../../Assessment/Plan/RaterGrid/RaterGridAdd";
import {
  getFutureVersions,
  getPastVersions,
} from "../../../LearningOutcomes/utils";
import RubricMappingModal from "../RubricMappingModal";
import { RefreshContext } from "./ApprovedOutcomeToggle";

export const OutcomeDetails = ({ outcome }) => {
  const { t } = useTranslation();

  return (
    <>
      <RatingScaleDetails outcome={outcome} />
      {!outcome.unapproved && (
        <>
          <ToggleDetails size="small" summary={t("analyze.variations")}>
            <VariantList variants={outcome.variants} />
          </ToggleDetails>
          <Versions outcome={outcome} />
          <Versions outcome={outcome} past />
        </>
      )}
    </>
  );
};

const RatingScaleDetails = ({ outcome }) => {
  const { t } = useTranslation();
  const { refreshLOs } = useContext(RefreshContext);
  const { ratingScales } = useContext(MasteryLevelContext);
  const [trigger, setTrigger] = useState(false);
  const [currentOutcome, setCurrentOutcome] = useState(outcome);

  useEffect(() => {
    if (trigger) {
      refreshLOs();
      setTrigger(false);
    }
  }, [trigger]);

  const findMappedRatingScale = () => {
    // Find default rating scale, if it exists
    const defaultScale = currentOutcome.rating_scales.find(
      (scale) => scale.is_default
    );
    if (defaultScale) {
      return ratingScales.find(
        (scale) => scale.id === defaultScale.rating_to_mastery_level_map
      );
    }
    // Otherwise, find the first mapped rating scale
    for (let i = 0; i < currentOutcome.rating_scales.length; i++) {
      const mapping = ratingScales.find(
        (mapping) =>
          mapping.id === outcome.rating_scales[i].rating_to_mastery_level_map
      );
      if (mapping && mapping.is_mapped) {
        return mapping;
      }
    }
    return null;
  };

  const mapping = findMappedRatingScale();

  return (
    <ToggleDetails size="small" summary={t("analyze.rubricDetails")}>
      {currentOutcome.rating_scales.length === 0 ? (
        <Alert variant="warning">{t("analyze.rubricAlert")}</Alert>
      ) : !mapping ? (
        <Alert variant="warning">
          {t("learningOutcomes.alignedToUnmapped")}
        </Alert>
      ) : (
        <>
          <Text weight="bold">{t("ratingScale.currentRatingScale")}: </Text>
          <Text lineHeight="double">
            {mapping?.title}{" "}
            {currentOutcome.rating_scales.find((scale) => scale.is_default) && (
              <Pill color="success">{t("masteryLevels.default")}</Pill>
            )}
          </Text>
          <RaterGridAdd
            outcome={currentOutcome}
            masteryMapping={mapping}
            adding={false}
          />
        </>
      )}
      <EditRatingScaleButton
        outcome={currentOutcome}
        setOutcome={setCurrentOutcome}
        setTrigger={setTrigger}
        mapping={mapping}
      />
    </ToggleDetails>
  );
};

const EditRatingScaleButton = ({
  outcome,
  setOutcome,
  setTrigger,
  mapping,
}) => {
  const { t } = useTranslation();
  const { archived } = useContext(RefreshContext);
  const [rubricModalOpen, setRubricModalOpen] = useState(false);

  return (
    <>
      {!archived && (
        <Button
          size="small"
          color="primary"
          margin="small 0"
          renderIcon={IconEditLine}
          onClick={() => setRubricModalOpen(true)}
        >
          {t("masteryLevels.editRatingScale")}
        </Button>
      )}
      <RubricMappingModal
        open={rubricModalOpen}
        setOpen={setRubricModalOpen}
        outcome={outcome}
        setOutcome={setOutcome}
        setRubricTrigger={setTrigger}
        selectedMapping={mapping}
      />
    </>
  );
};

const VariantList = ({ variants }) => {
  const { t } = useTranslation();

  return variants.length > 0 ? (
    <List isUnstyled>
      {variants.map((variant, key) => (
        <List.Item key={key}>
          <Text color="brand" size="small">
            <Flex width="100%" alignItems="start">
              <Flex.Item shouldGrow shouldShrink>
                {variant.statement}
              </Flex.Item>
              <Flex.Item>
                {new Date(variant.last_updated).toLocaleDateString()}
              </Flex.Item>
            </Flex>
            <div style={{ marginLeft: 15 }}>
              <OutcomeDetails outcome={variant} />
            </div>
          </Text>
        </List.Item>
      ))}
    </List>
  ) : (
    <Text size="small">{t("analyze.noVariants")}</Text>
  );
};

const Versions = ({ outcome, past }) => {
  const { t } = useTranslation();
  const versions = past ? getPastVersions(outcome) : getFutureVersions(outcome);

  return (
    (past || versions.length > 0) && (
      <ToggleDetails
        size="small"
        summary={
          past
            ? t("review.versionHistory")
            : t("learningOutcomes.futureVersions")
        }
      >
        {versions.length > 0 ? (
          <List isUnstyled>
            {versions.map((version, key) => (
              <List.Item key={key}>
                <Text color="brand" size="small">
                  <Flex width="100%" alignItems="start">
                    <Flex.Item shouldGrow shouldShrink>
                      {version.statement}
                    </Flex.Item>
                    <Flex.Item>
                      {version.effective_term?.start_at
                        ? new Date(
                            version.effective_term.start_at
                          ).toLocaleDateString()
                        : new Date(version.last_updated).toLocaleDateString()}
                    </Flex.Item>
                  </Flex>
                  {past && (
                    <div style={{ marginLeft: 15 }}>
                      <HistoryOutcomeDetails outcome={version} />
                    </div>
                  )}
                </Text>
              </List.Item>
            ))}
          </List>
        ) : (
          <Text size="small">{t("analyze.noHistory")}</Text>
        )}
      </ToggleDetails>
    )
  );
};

const HistoryOutcomeDetails = ({ outcome }) => {
  const { t } = useTranslation();
  const { ratingScales } = useContext(MasteryLevelContext);

  const findMappedRatingScale = () => {
    // Find default rating scale, if it exists
    const defaultScale = outcome.rating_scales.find(
      (scale) => scale.is_default
    );
    if (defaultScale) {
      return ratingScales.find(
        (scale) => scale.id === defaultScale.rating_to_mastery_level_map
      );
    }
    // Otherwise, find the first mapped rating scale
    for (let i = 0; i < outcome.rating_scales.length; i++) {
      const mapping = ratingScales.find(
        (mapping) =>
          mapping.id === outcome.rating_scales[i].rating_to_mastery_level_map
      );
      if (mapping && mapping.is_mapped) {
        return mapping;
      }
    }
    return null;
  };

  const mapping = findMappedRatingScale();

  return (
    <Grid>
      <Grid.Row>
        <Grid.Col>
          <ToggleDetails size="small" summary={t("analyze.rubricDetails")}>
            {outcome.rating_scales.length === 0 ? (
              <Alert variant="warning">{t("analyze.rubricAlert")}</Alert>
            ) : (
              <>
                <Text weight="bold">
                  {t("ratingScale.currentRatingScale")}:
                </Text>
                <Text>{mapping?.title}</Text>
                <RaterGridAdd
                  outcome={outcome}
                  masteryMapping={mapping}
                  adding={false}
                />
              </>
            )}
          </ToggleDetails>
          <ToggleDetails size="small" summary={t("analyze.variations")}>
            <VariantList variants={outcome.variants} />
          </ToggleDetails>
        </Grid.Col>
      </Grid.Row>
    </Grid>
  );
};
